import DescriptionIcon from "@mui/icons-material/Description";
import { Box, Button, Divider, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useHttp } from "../../hooks/useHttp";
import { Image } from "../../models/images";
import { Stats } from "../../models/stats";
import { initialization_status, Pipeline, PipelineSteps, Task, TaskStatus, TaskStatusType } from "../../models/tasks";
import { LayoutMode, StatsMap } from "../../pages/Tasks/Tasks";
import LinearProgressWithLabel from "../LinearProgressWithLabel/LinearProgressWithLabel";
import { message_map, next_message_map } from "./message_config";
import { OrganVisualization } from "./OrganVisualization";
import "./TaskComponent.scss";
import { HelpType } from "../Help/Help";

const lungs = require("../../images/lungs.png");
const tumor = require("../../images/tumor.png");
const background = require("../../images/background.png");
const background_2 = require("../../images/background_2.png");

interface TaskComponentInterface {
  task: Task;
  refresh: () => void;
  pipelineStats: StatsMap | null;
  layoutMode: LayoutMode;
}

const organs = ["all", "aorta", "left_lung", "right_lung", "trachea", "pulmonary_artery"];
const pretty_organ_name: { [id: string]: string } = {
  aorta: "Aorta",
  left_lung: "Left lung",
  right_lung: "Right lung",
  trachea: "Trachea",
  pulmonary_artery: "Pulmonary artery",
  all: "Lungs segmentation",
};

const pretty_nodule_name = (nodule: string) => {
  if (nodule === "all") {
    return "Lungs with nodules";
  }
  return (nodule.charAt(0).toUpperCase() + nodule.slice(1)).replace("_", " ");
};

const pretty_task_name: { [id: string]: string } = {
  [Pipeline.LUNGS_REPORT.valueOf()]: "Lungs segmentation report",
  [Pipeline.NODULES.valueOf()]: "Nodules report",
};

export const TaskComponent = ({ task, refresh, pipelineStats, layoutMode }: TaskComponentInterface) => {
  const [downloadButtonVisible, setDownloadButtonVisible] = useState<boolean>(false);

  const { trigger: downloadLungsReport } = useHttp<any>("/lungsTasks/report");
  const { trigger: downloadNodulesReport } = useHttp<any>("/nodulesTasks/report");
  const { trigger: downloadOrganImage } = useHttp<any>("/lungsTasks/organ", {}, true);
  const { trigger: downloadNoduleImage } = useHttp<any>("/nodulesTasks/nodule", {}, true);
  const { trigger: getNoduleNamesFunc } = useHttp<any>("/nodulesTasks/noduleNames", {}, true);
  const { trigger: getImageById } = useHttp<any>("/images/", {}, true);

  const [image, setImage] = useState<Image | null>(null);
  const { trigger: fetchImages } = useHttp<Image[]>("/images");
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [scene, setScene] = useState<string | null>(null);
  const [images, setImages] = useState<{ [key: string]: string }>({});
  const [nodulesFound, setNodulesFound] = useState<boolean>(true);

  useEffect(() => {
    const cachedImages = sessionStorage.getItem("cachedImages");
    if (cachedImages) {
      const parsedImages: Image[] = JSON.parse(cachedImages);
      const image = parsedImages.find((img: Image) => img.id === task.image.id);
      if (image) setImage(image);
      else {
        getImageById({
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            id: task.image.id,
            sessionId: sessionStorage.getItem("sessionId") || "",
          },
        }).then((response) => {
          if (response) {
          }
        });
      }
    } else {
      fetchImages({
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          sessionId: sessionStorage.getItem("sessionId") || "",
        },
      }).then((response) => {
        if (response) {
          sessionStorage.setItem("cachedImages", JSON.stringify(response));
        }
      });
    }
  }, [fetchImages]);

  const downloadPdf = async () => {
    const downloadFunc = task.pipeline === Pipeline.LUNGS_REPORT ? downloadLungsReport : downloadNodulesReport;
    const response: any = await downloadFunc({
      method: "GET",
      responseType: "blob",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        id: task.id,
      },
    });

    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "report.pdf");
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const fetchImage = async (imageName: string) => {
    setImages({});
    try {
      const func = task.pipeline === Pipeline.LUNGS_REPORT ? downloadOrganImage : downloadNoduleImage;
      const response = await func({
        method: "GET",
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          taskId: task.id,
          imageName: imageName,
        },
      });

      if (response && response?.data) {
        const url = window.URL.createObjectURL(response.data);
        setImages((prev) => {
          const updatedImages = { ...prev, [imageName]: url };
          if (task.pipeline === Pipeline.LUNGS_REPORT) {
            const sortedImages: { [key: string]: string } = {};
            organs.forEach((organ) => {
              if (updatedImages[organ]) {
                sortedImages[organ] = updatedImages[organ];
              }
            });
            Object.keys(updatedImages).forEach((key) => {
              if (!sortedImages[key]) {
                sortedImages[key] = updatedImages[key];
              }
            });
            return sortedImages;
          }
          return updatedImages;
        });
      }
    } catch (error) {
      console.error(`Failed to fetch image for ${imageName}:`, error);
    }
  };

  const getNoduleNames = async () => {
    try {
      const response = await getNoduleNamesFunc({
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          taskId: task.id,
        },
      });
      const data = response.data.map((item: string) => item.replace(/ /g, "_"));
      return data;
    } catch (error) {
      console.error("Failed to fetch nodule names:", error);
    }
  };

  useEffect(() => {
    if (task.completed) {
      if (task.pipeline === Pipeline.LUNGS_REPORT) {
        organs.forEach((organ) => {
          fetchImage(organ);
        });
      } else {
        getNoduleNames().then((r) => {
          if (r instanceof Array)
            r.forEach((noduleName: string) => {
              fetchImage(noduleName);
            });
        });
      }
    }
  }, [task.completed, task.id]);

  useEffect(() => {
    setDownloadButtonVisible(false);
    if (task.statuses.length > 0) {
      const last_status = task.statuses[task.statuses.length - 1];
      if (last_status.status === TaskStatusType.COMPLETED) setDownloadButtonVisible(true);
    }
  }, [task, task.statuses, task.statuses.length]);

  const handleFullscreenToggle = () => {
    setFullscreenOpen(!fullscreenOpen);
  };

  return (
    <div className="taskComponent">
      <Box
        className="taskComponent-header"
        style={{
          background: `url(${background})`,
          filter: task.pipeline === Pipeline.NODULES ? "hue-rotate(30deg)" : "hue-rotate(-20deg)",
        }}
      >
        <img
          src={task.pipeline === Pipeline.LUNGS_REPORT ? lungs : tumor}
          alt="Task Icon"
          className="taskComponent-header-icon"
        />
        <Typography variant={"h4"}>{pretty_task_name[task.pipeline]}</Typography>
        <Divider
          flexItem
          style={{ width: "100%", height: "1px", backgroundColor: "white", opacity: 0.2, margin: ".5rem 0" }}
        />
        <Typography variant={"h2"}>{task.name}</Typography>
      </Box>
      <div className="taskComponent-body">
        <div className="taskComponent-body-left">
          {task.description !== "" && (
            <div className="taskComponent-body-left-description">
              <div className="taskComponent-title">
                <p>Description</p>
              </div>
              <p>{task.description}</p>
            </div>
          )}
          <TaskComponentStatus
            statuses={task.statuses}
            completed={task.completed}
            completeTask={() => (task.completed = true)}
            pipelineStats={pipelineStats}
            downloadPdf={downloadPdf}
            pipeline={task.pipeline}
            setNodulesFound={setNodulesFound}
            nodulesFound={nodulesFound}
          />
          {layoutMode >= LayoutMode.MOVE_IMAGES && (
            <div className="taskComponent-body-right-image">
              <div className="taskComponent-title">
                <p>Image</p>
              </div>
              <div className="taskComponent-body-right-image-caption">
                <p>{task.image.name}</p>
              </div>
              <img src={image ? image.base64Image : undefined} alt="" />
            </div>
          )}
        </div>
        <div className="taskComponent-body-right">
          <OrganVisualization
            task={task}
            organImages={images}
            pretty_name={task.pipeline === Pipeline.LUNGS_REPORT ? pretty_organ_name : pretty_nodule_name}
            setScene={setScene}
            scene={scene}
            nodulesFound={nodulesFound}
          />
          {layoutMode < LayoutMode.MOVE_IMAGES && (
            <div className="taskComponent-body-right-image">
              <div className="taskComponent-title">
                <p>Image</p>
              </div>
              <div className="taskComponent-body-right-image-caption">
                <p>{task.image.name}</p>
              </div>
              <img src={image ? image.base64Image : undefined} alt="" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TaskComponentStatus = ({
  statuses,
  completed,
  pipelineStats,
  completeTask,
  downloadPdf,
  pipeline,
  nodulesFound,
  setNodulesFound,
}: {
  statuses: TaskStatus[];
  completed: boolean;
  pipelineStats: StatsMap | null;
  completeTask: () => void;
  downloadPdf: () => void;
  pipeline: Pipeline;
  nodulesFound: boolean;
  setNodulesFound: (value: boolean) => void;
}) => {
  const isEmpty = statuses.length === 0;
  const isTerminated = statuses.length > 0 ? statuses[statuses.length - 1].status === TaskStatusType.TERMINATED : false;
  const [processedSteps, setProcessedSteps] = useState<number>(0);

  useEffect(() => {
    if (statuses.length > 0) {
      let counter = 0;
      statuses.forEach((status) => {
        if (status.status === TaskStatusType.BETWEEN_STAGES || status.status === TaskStatusType.COMPLETED) {
          counter++;
        }
      });
      setProcessedSteps(counter);
    }
  }, [statuses, statuses.length]);

  useEffect(() => {
    if (statuses.length > 0) {
      const lastStatus = statuses[statuses.length - 1];
      if (lastStatus.status === TaskStatusType.COMPLETED && lastStatus.stage === PipelineSteps.SEGMENTED) {
        setNodulesFound(false);
      } else if (lastStatus.status === TaskStatusType.COMPLETED) {
        setNodulesFound(true);
      }
    }
  }, [statuses, setNodulesFound, statuses.length]);

  if (isEmpty) {
    initialization_status.createdAt = new Date().toLocaleString();
    return (
      <div className="taskComponent-statuses">
        <TaskComponentStatusItem status={initialization_status} loading={true} stats={null} pipeline={pipeline} />
      </div>
    );
  } else
    return (
      <div className="taskComponent-statuses">
        <div className="taskComponent-title">
          <p>Processing status</p>
        </div>
        <div
          className="taskComponent-statuses-totalStatus"
          style={{
            background: `url(${background_2})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "cover",
            display: "flex",
            transition: "2s",
            filter: isTerminated
              ? "grayscale(20%) hue-rotate(140deg)"
              : completed
                ? ""
                : "grayscale(20%) hue-rotate(180deg)",
          }}
        >
          <p>{isTerminated ? "Terminated" : completed ? "Finished " : "Pending"}</p>
          {completed && !isTerminated && nodulesFound && (
            <Button startIcon={<DescriptionIcon />} onClick={() => downloadPdf()}>
              Download report
            </Button>
          )}
          {completed && !isTerminated && !nodulesFound && (
            <Button startIcon={<DescriptionIcon />} onClick={() => downloadPdf()} disabled>
              No nodules found
            </Button>
          )}
          {completed && isTerminated && <h1>{statuses[statuses.length - 1].message}</h1>}
          {!completed && (
            <Button startIcon={<DescriptionIcon />} onClick={() => downloadPdf()} disabled>
              Download report
            </Button>
          )}
        </div>
        <Divider className="taskComponent-statuses-divider" />
        <p className="taskComponent-statuses-subtitle">Processing steps {processedSteps} / 4</p>
        {statuses.map((status: TaskStatus, index: number) => {
          const isLast = index === statuses.length - 1;
          const nextStage = Object.keys(PipelineSteps).indexOf(status.stage) + 1;
          const nextStageKey = Object.keys(PipelineSteps)[nextStage];
          const stats = pipelineStats ? pipelineStats[nextStageKey] : null;

          if (isLast && status.status === TaskStatusType.PROCESSING) {
            return (
              <Box key={status.id ?? index}>
                {index !== 1 && index !== 0 && <Divider />}
                <TaskComponentStatusItem status={status} loading={true} pipeline={pipeline} stats={stats} />
              </Box>
            );
          }

          if (
            isLast &&
            (status.status === TaskStatusType.FAILED ||
              status.status === TaskStatusType.TERMINATED ||
              status.status === TaskStatusType.COMPLETED)
          ) {
            completeTask();
            return (
              <Box key={status.id ?? index}>
                {index !== 1 && index !== 0 && <Divider />}
                <TaskComponentStatusItem status={status} loading={false} pipeline={pipeline} stats={stats} />
              </Box>
            );
          }

          if (status.status === TaskStatusType.BETWEEN_STAGES) {
            return (
              <Box key={status.id ?? index}>
                {index !== 1 && index !== 0 && <Divider />}
                <TaskComponentStatusItem status={status} loading={false} pipeline={pipeline} stats={stats} />
              </Box>
            );
          } else return null;
        })}
      </div>
    );
};

interface TaskComponentStatusItemInterface {
  status: TaskStatus;
  loading: boolean;
  pipeline: Pipeline;
  stats: Stats | null;
}

const TaskComponentStatusItem = ({ status, loading, pipeline, stats }: TaskComponentStatusItemInterface) => {
  const statusClass =
    status.status === TaskStatusType.TERMINATED
      ? "terminated"
      : status.status === TaskStatusType.FAILED
        ? "failed"
        : loading
          ? "pending"
          : "completed";
  const [currentProcessingTime, setCurrentProcessingTime] = useState<number>(status.processingTime);

  useEffect(() => {
    if (loading) {
      let initialOffset = (Date.now() - new Date(status.createdAt).getTime()) / 1000;
      if (initialOffset > 0) {
        initialOffset = 0;
      }
      const interval = setInterval(() => {
        const timeDifference = (Date.now() - new Date(status.createdAt).getTime()) / 1000;
        setCurrentProcessingTime(timeDifference - initialOffset);
      }, 500);

      return () => clearInterval(interval);
    } else {
      setCurrentProcessingTime(Math.round(status.processingTime));
    }
  }, [loading, status]);

  const medianProcessingTime = stats?.medianProcessingTime;

  const isOverMedian = medianProcessingTime && currentProcessingTime > medianProcessingTime;
  const progressBarColor = isOverMedian ? "error" : "primary";
  const title =
    status.status === TaskStatusType.FAILED ||
    status.status === TaskStatusType.TERMINATED ||
    status.status === TaskStatusType.PROCESSING
      ? next_message_map[pipeline][status.stage]
      : message_map[pipeline][status.stage];
  return (
    <div className={"taskComponent-status taskComponent-status-" + statusClass}>
      <Box className="taskComponent-status-info">
        <Box className="taskComponent-status-info-item">
          <Typography variant={"h3"}>{title}</Typography>
          <Typography className="taskComponent-status-info-item-message" variant={"body2"}>
            {status.message}
          </Typography>
        </Box>
        {loading && (
          <Box className="taskComponent-status-info-loading">
            <LinearProgressWithLabel
              variant="determinate"
              value={currentProcessingTime}
              mean={medianProcessingTime}
              color={progressBarColor}
            />
          </Box>
        )}
        <Box className="taskComponent-status-info-right">
          {!loading && status.status !== TaskStatusType.FAILED && status.status !== TaskStatusType.TERMINATED && (
            <Typography variant={"subtitle2"}>Processed for: {currentProcessingTime}s</Typography>
          )}
          <Typography variant={"subtitle2"} className="taskComponent-status-info-item-createdAt">
            Today{" "}
            {new Date(status.createdAt).toLocaleString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            })}
          </Typography>
        </Box>
      </Box>
    </div>
  );
};

// const TaskComponentInfoItem = ({title, value}: { title: string; value: string }) => {
//     return (
//         <Box className="taskComponent-info-item">
//             <Typography variant={"body1"}>{title}</Typography>
//             <Typography variant={"body1"}>{value}</Typography>
//         </Box>
//     );
// };
